import React, { useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { GoogleMap, InfoWindowF, MarkerF } from "@react-google-maps/api";
import { CARRIER_NAMES } from "@utils/globalConsts";
import { formatOpeningTimes } from "@utils/times";
import CollectPlusLogo from "assets/images/carrier-logos/collectplus.png";
import HermesLogo from "assets/images/carrier-logos/hermes.svg";
import GreenTick from "assets/images/icons/green-tick.svg";
import RedCross from "assets/images/icons/red-cross.png";
import CollectplusMarker from "assets/images/markers/collectplus.png";
import HermesMarker from "assets/images/markers/hermes.png";
import useGoogleMaps from "hooks/useGoogleMaps";
import { CarrierPoint, useGetCarrierPointsQuery } from "reduxStore/services/carrier-points-api/api";
import { Location } from "types/locations";

import Toggle from "components/atoms/Toggle";
import LocationCookieAutoComplete from "components/molecules/LocationCookieAutoComplete";


const LIBRARIES_TO_LOAD = [ "places" ];

const MapWrapper = ({ children }: { children: React.ReactNode }) => {
  const [ cookies, setCookie ] = useCookies<"location", { location: Location | null }>([ "location" ]);
  const { isLoaded: isGoogleMapsLoaded } = useGoogleMaps();

  if (!isGoogleMapsLoaded) {
    return null;
  }

  return <div className={"overflow-hidden w-full h-full border-2"}>
    <GoogleMap
      mapContainerStyle={{
        width: "100%",
        height: "100%"
      }}
      center={{
        lat: cookies.location?.lat || 54,
        lng: cookies.location?.lng || -2
      }}
      zoom={cookies.location?.lat ? 14 : 5}
      onLoad={() => {}}
      onUnmount={() => {}}
      options={{
        scrollwheel: true,
        draggable: true,
        clickableIcons: false,
        zoomControl: true,
        disableDoubleClickZoom: true,
        disableDefaultUI: true,
      }}
    >
      {children}
    </GoogleMap>
  </div>;
};

const InnerDropPointFinder = () => {
  const [ cookies ] = useCookies<"location", { location: Location | null }>([ "location" ]);
  const [ autocomplete ] = useState<any>(null);
  const [ selectedLocation, setSelectedLocation ] = useState<CarrierPoint | null>(null);

  const [ showHermes, setShowHermes ] = useState(true);

  const { data: carrierPoints } = useGetCarrierPointsQuery(
    {
      lat: cookies.location?.lat || 0,
      lng: cookies.location?.lng || 0,
    },
    { skip: !(cookies.location?.lat && cookies.location?.lng) }
  );

  useEffect(() => {
    document.title = "DropPoint | DropPoint Finder";
  }, []);

  useEffect(() => {
    if (cookies.location?.name) {
      setSelectedLocation(null);
    }
  }, [ cookies.location?.name, autocomplete ]);


  return <>
    <LocationCookieAutoComplete className={`
    absolute top-24 sm:top-4 left-1/2 -translate-x-1/2
    placeholder-dpGray outline-0 border-1 border-dpGray
    px-4 py-2 rounded-lg shadow-inner-lg`}/>
    <div className="absolute left-0 right-0 top-0 sm:right-auto bg-white p-2 sm:p-4 border-b-2 sm:border-r-2 border-dpLightGray">
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-row items-center">
          <img src={HermesMarker} className="h-8 mr-2" alt="Hermes marker icon" />
          <div>{CARRIER_NAMES.hermes}</div>
        </div>
        <Toggle checked={showHermes} onChange={setShowHermes} className="ml-4"/>
      </div>
    </div>
    {
      carrierPoints && carrierPoints.map((point, i) => {
        if ((point.carrier === "hermes") && !showHermes) {
          return null;
        }

        return <MarkerF
          key={`${point.lat},${point.lng}`}
          position={{
            lat: point.lat,
            lng: point.lng
          }}
          visible={true}
          draggable={false}
          clickable={true}
          icon={{
            url: point.carrier === "hermes" ? HermesMarker : CollectplusMarker,
            scaledSize: {
              width: 37,
              height: 44,
              equals: () => true
            }
          }}
          onClick={() => {
            setSelectedLocation(point);
          }}
        />;
      })
    }
    {
      selectedLocation && (
        <InfoWindowF
          position={{
            lat: selectedLocation.lat,
            lng: selectedLocation.lng
          }}
          onCloseClick={() => setSelectedLocation(null)}
        >
          <div className="relative font-lato">
            <img
              src={selectedLocation.carrier === "hermes" ? HermesLogo : CollectPlusLogo}
              className="h-4 mb-4"
              alt=""
            />
            <div className="font-bold">{selectedLocation.name}</div>
            <div>{selectedLocation.address}</div>
            <div className="font-bold mt-2">Opening times:</div>
            <div
              dangerouslySetInnerHTML={{ __html: formatOpeningTimes(
                selectedLocation.openingTimes.map(time => time ? time[0] : null),
                null,
                false,
                "<br/>"
              ) }}
            />
            <div className="mt-2 flex flex-row items-center">
              <div className="font-bold mr-2">Disabled access:</div>
              <img
                src={selectedLocation.disabledAccess ? GreenTick : RedCross}
                className="w-6 mt-1"
                alt=""
              />
            </div>
          </div>
        </InfoWindowF>
      )
    }
  </>;
};

export default function DropPointFinder () {
  return <MapWrapper>
    <InnerDropPointFinder />
  </MapWrapper>;
}
